import { useMemo } from 'react'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { getAllOffers } from 'selectors/offerSelectors'
import { arrayToMap } from 'lib/array/arrayUtils'
import { isCartPanelAccommodationUnavailableStatus, resolveItemPrice } from '../utils/cartPanelUtils'
import { isLEHotel } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import type { CartPanelAccommodationPricingInput } from '../utils/tripChatCartPricingInputUtils'

export type PricingInput = CartPanelAccommodationPricingInput

export interface ResolvedItemPricing {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
  isAvailable: boolean | undefined
  loading: boolean
  error: boolean
  offer: App.HotelOffer | undefined
}

export function useItineraryItemPricing(
  items: Array<PricingInput>,
): Map<number, ResolvedItemPricing> {
  const { offers, offersLoading, offerErrors, calendarsByOccupancy, availableRatesByOccupancy } = useAppSelector(state => ({
    offers: getAllOffers(state),
    offersLoading: state.offer.offersLoading,
    offerErrors: state.offer.offerErrors,
    calendarsByOccupancy: state.calendar.calendarsByOccupancy,
    availableRatesByOccupancy: state.offer.offerAvailableRatesByOccupancy,
  }), shallowEqual)

  return useMemo(() => {
    return arrayToMap(items, item => item.tripItemId, item => {
      const rawOffer = offers[item.offerId]
      const offer = rawOffer && isLEHotel(rawOffer) ? rawOffer : undefined
      const loading = !!offersLoading[item.offerId]
      const error = !!offerErrors[item.offerId]

      if (isCartPanelAccommodationUnavailableStatus(item.cartStatus)) {
        return {
          price: item.savedPrice,
          memberPrice: item.savedMemberPrice,
          value: item.savedValue,
          isAvailable: false,
          loading: false,
          error: false,
          offer,
        }
      }

      if (offer && item.packageId && item.checkIn && item.checkOut && item.occupancy) {
        const uniqueKey = getPackageUniqueKey(item.packageId, item.duration ?? 0, item.roomRateId ?? '')
        const pkg = offer.packages.find(p => p.uniqueKey === uniqueKey)

        if (pkg) {
          const itemTotals = getLEPackageItemTotals(
            offer,
            pkg,
            item.checkIn,
            item.checkOut,
            item.occupancy,
            undefined,
            calendarsByOccupancy,
            availableRatesByOccupancy,
          )

          if (itemTotals) {
            const displayTotals = getUserCartItemDisplayTotals(itemTotals)
            return {
              price: displayTotals.publicTotal,
              memberPrice: displayTotals.memberTotal,
              value: itemTotals.value,
              isAvailable: itemTotals.isAvailable,
              loading,
              error,
              offer,
            }
          }
        }
      }

      const resolved = resolveItemPrice(offer, item.packageId)

      return {
        price: resolved.price ?? item.savedPrice,
        memberPrice: resolved.memberPrice ?? item.savedMemberPrice,
        value: resolved.value ?? item.savedValue,
        isAvailable: undefined,
        loading: loading || (!offer && !error),
        error,
        offer,
      }
    })
  }, [items, offers, offersLoading, offerErrors, calendarsByOccupancy, availableRatesByOccupancy])
}
